import { Link } from 'react-router';
import { useList } from '@refinedev/core';
import { describeRole, type MeResponse } from 'shared';
import { useSession } from '../../lib/session.js';
import { ScopePanel } from '../../components/ScopePanel.js';
import { BalanceTable } from '../../components/BalanceTable.js';
import { OpenChargesTable, type OpenChargeRow } from '../../components/OpenChargesTable.js';
import { StatGrid, type Stat } from '../../components/StatGrid.js';
import { HealthBadge } from '../../components/HealthBadge.js';
import { Widget } from '../../components/Widget.js';
import { ReportsGlanceWidget } from '../../components/ReportsGlanceWidget.js';
import { ExceptionsWidget } from '../../components/ExceptionsWidget.js';
import { DashboardGrid, type DashboardItem } from '../../components/DashboardGrid.js';

/**
 * Four roles, four landing pages.
 *
 * They are variations on one set of facts rather than four separate applications:
 * the numbers all come from the same `/api/me` call, and which of them a role sees
 * is a product decision about attention, not about access. Access was decided by the
 * policies before this component ran — an accountant asking for the delinquency list
 * and a maintenance tech asking for the same thing both get their own scope back.
 *
 * The dispatch covers every value of `StaffRole` plus the null case. A fifth role
 * added to the enum would land here with nothing rendered but the heading, which is
 * the reason the null branch says so out loud rather than returning null.
 *
 * Each role dashboard builds a `DashboardItem[]` — id, a fixed column span, and the
 * content — rather than a flat JSX fragment, and renders it through one shared
 * `DashboardGrid`. That's what makes the layout both multi-column and
 * drag-reorderable: order is the one thing a viewer can change (persisted per role,
 * see `lib/dashboardLayout.ts`); span is a fixed per-widget layout decision, not
 * something to reorder or resize. `HealthBadge` and the welcome heading stay outside
 * the grid — they aren't "dashboard elements" a staff member would think to move.
 */
export function StaffDashboard() {
  const { state } = useSession();
  if (state.status !== 'signed-in') return null;

  const { me } = state;
  const role = me.actor.staffRole;

  return (
    <section className="stack">
      <div>
        <h1>Welcome {me.actor.name}</h1>
        <p className="muted">Signed in as {describeRole('STAFF', role)}.</p>
      </div>

      {role === 'ADMIN' ? <AdminDashboard me={me} /> : null}
      {role === 'PROPERTY_MANAGER' ? <ManagerDashboard me={me} /> : null}
      {role === 'ACCOUNTING' ? <AccountingDashboard me={me} /> : null}
      {role === 'MAINTENANCE' ? <MaintenanceDashboard me={me} /> : null}
      {role === 'DEVELOPER' ? <DeveloperDashboard me={me} /> : null}
      {role === null ? (
        <p className="alert alert--error">
          This staff account has no role, so it can do nothing. That shape should be impossible — an
          administrator needs to look at the user row.
        </p>
      ) : null}

      <HealthBadge />
    </section>
  );
}

/** The counts every role sees, in the order that role cares about them. */
function inventoryStats(me: MeResponse, options: { linked: boolean }): Stat[] {
  const link = (to: string) => (options.linked ? to : undefined);
  return [
    { label: 'Properties', value: me.scope.properties, to: link('/staff/properties') },
    { label: 'Units', value: me.scope.units, to: link('/staff/properties') },
    { label: 'Leases', value: me.scope.leases, to: link('/staff/leases') },
    { label: 'Tenants', value: me.scope.tenants, to: link('/staff/tenants') },
  ];
}

/**
 * Per-charge detail behind the "Open charges" stat — rent, a late fee, and a
 * utility bill on the same lease are three rows here, not one, since each is
 * its own obligation (invariant 3). Fetched separately from `/api/me` rather
 * than folded into it: that endpoint is an orientation surface reporting the
 * caller's own scoped counts, not a growing product payload.
 */
function OpenChargesWidget() {
  const { data, isLoading, error } = useList<OpenChargeRow>({
    resource: 'ledger/open-charges',
    pagination: { mode: 'off' },
  });

  return (
    <Widget
      icon="ledger"
      tone="green"
      title="Open charges"
      action={<Link to="/staff/open-charges">By lease →</Link>}
    >
      {isLoading ? <p className="muted">Loading…</p> : null}
      {error ? (
        <p className="alert alert--error">
          {error instanceof Error ? error.message : 'Could not load open charges.'}
        </p>
      ) : null}
      {!isLoading && !error ? (
        <OpenChargesTable
          charges={data?.data ?? []}
          emptyMessage="Nothing open."
          caption="Each row is one obligation, not one lease — rent and a late fee on the same lease are two rows."
        />
      ) : null}
    </Widget>
  );
}

function AdminDashboard({ me }: { me: MeResponse }) {
  const items: DashboardItem[] = [
    {
      id: 'stats',
      span: 'full',
      content: (
        <StatGrid
          stats={[
            ...inventoryStats(me, { linked: true }),
            {
              label: 'Open charges',
              value: me.scope.openCharges,
              to: '/staff/open-charges',
              hint: 'Total charges with pending settlement.',
            },
          ]}
        />
      ),
    },
    { id: 'exceptions', span: 'half', content: <ExceptionsWidget /> },
    {
      id: 'delinquency',
      span: 'half',
      content: (
        <Widget
          icon="ledger"
          tone="orange"
          title="Delinquency"
          action={<Link to="/staff/delinquency">Full report →</Link>}
        >
          <BalanceTable
            balances={me.balances}
            emptyMessage="No lease is carrying a balance."
            caption="These leases are carrying a balance that is past their grace period."
          />
        </Widget>
      ),
    },
    { id: 'open-charges', span: 'half', content: <OpenChargesWidget /> },
    { id: 'reports-glance', span: 'full', content: <ReportsGlanceWidget /> },
    {
      id: 'administration',
      span: 'half',
      content: (
        <Widget icon="shield" tone="blue" title="Administration">
          <p className="muted">
            <Link to="/staff/audit-log">The audit log</Link> is limited to this role. It is the
            record of what every other staff member did, which is a poor thing to make readable by
            the people it is evidence about.
          </p>
          <p className="muted">
            Note what this role still cannot do: the admin account is immutable through the
            application. Rotating its password, disabling it, or moving it to someone else are
            migrator-role operations that need shell access to the VPS. That is deliberate — the
            alternative is an endpoint that can mint an administrator.
          </p>
        </Widget>
      ),
    },
    {
      id: 'diagnostics',
      span: 'full',
      content: (
        <div>
          <h2>Diagnostics</h2>
          <details>
            <summary>What this account can see, per table</summary>
            <div className="card">
              <ScopePanel me={me} />
            </div>
          </details>
        </div>
      ),
    },
  ];

  return <DashboardGrid storageKey="dashboard:ADMIN" items={items} />;
}

/**
 * Same shape as AdminDashboard — same stats, same widgets, same Payment
 * Center visibility, since this role's whole point is "see everything ADMIN
 * sees." Not a straight reuse of `AdminDashboard`, though: that function's
 * "Administration" widget is written about *the* admin account specifically
 * ("the admin account is immutable through the application... rotating its
 * password... are migrator-role operations"), which reads as false/confusing
 * shown to a DEVELOPER account that isn't that row. This one states this
 * role's own actual boundary instead — same "explain what this role can and
 * cannot do" convention every other dashboard here already follows.
 */
function DeveloperDashboard({ me }: { me: MeResponse }) {
  const items: DashboardItem[] = [
    {
      id: 'stats',
      span: 'full',
      content: (
        <StatGrid
          stats={[
            ...inventoryStats(me, { linked: true }),
            {
              label: 'Open charges',
              value: me.scope.openCharges,
              to: '/staff/open-charges',
              hint: 'Total charges with pending settlement.',
            },
          ]}
        />
      ),
    },
    { id: 'exceptions', span: 'half', content: <ExceptionsWidget /> },
    {
      id: 'delinquency',
      span: 'half',
      content: (
        <Widget
          icon="ledger"
          tone="orange"
          title="Delinquency"
          action={<Link to="/staff/delinquency">Full report →</Link>}
        >
          <BalanceTable
            balances={me.balances}
            emptyMessage="No lease is carrying a balance."
            caption="These leases are carrying a balance that is past their grace period."
          />
        </Widget>
      ),
    },
    { id: 'open-charges', span: 'half', content: <OpenChargesWidget /> },
    { id: 'reports-glance', span: 'full', content: <ReportsGlanceWidget /> },
    {
      id: 'role-info',
      span: 'half',
      content: (
        <Widget icon="shield" tone="blue" title="Your access">
          <p className="muted">
            Everything ADMIN can see, including the full Payment Center — rent roll, delinquency,
            owner disbursements, owner statements, payments history — with two exceptions. This role
            cannot approve or push an owner disbursement: the Approve, Record external settlement,
            and Retry payout actions do not render on that page for this role, and the API refuses
            those three requests outright. And this role cannot read encrypted sensitive data —
            tenant and owner SSNs and tax IDs — the same structural exclusion MAINTENANCE has,
            enforced by the database rather than by this page.
          </p>
          {me.scope.tenantSensitive > 0 ? (
            <p className="alert alert--error" role="alert">
              <strong>Stop.</strong> This account can read {me.scope.tenantSensitive} row(s) from{' '}
              <code>tenant_sensitive</code> and should read none. That is an RLS regression, not a
              display bug — check `app_can_read_sensitive()` before going further.
            </p>
          ) : null}
        </Widget>
      ),
    },
    {
      id: 'diagnostics',
      span: 'full',
      content: (
        <div>
          <h2>Diagnostics</h2>
          <details>
            <summary>What this account can see, per table</summary>
            <div className="card">
              <ScopePanel me={me} />
            </div>
          </details>
        </div>
      ),
    },
  ];

  return <DashboardGrid storageKey="dashboard:DEVELOPER" items={items} />;
}

function ManagerDashboard({ me }: { me: MeResponse }) {
  const items: DashboardItem[] = [
    {
      id: 'stats',
      span: 'full',
      content: (
        <StatGrid
          stats={[
            ...inventoryStats(me, { linked: true }),
            {
              label: 'Overdue notices',
              value: me.scope.overdueNotices,
              to: me.scope.overdueNotices > 0 ? '/staff/leases' : undefined,
              hint: 'past dueBy with nothing sent',
            },
          ]}
        />
      ),
    },
    { id: 'exceptions', span: 'half', content: <ExceptionsWidget /> },
    {
      id: 'delinquency',
      span: 'half',
      content: (
        <Widget
          icon="ledger"
          tone="orange"
          title="Delinquency"
          action={<Link to="/staff/rent-roll">Rent roll →</Link>}
        >
          <BalanceTable balances={me.balances} emptyMessage="No lease is carrying a balance." />
        </Widget>
      ),
    },
    {
      id: 'michigan-clocks',
      span: 'half',
      content: (
        <Widget icon="clock" tone="blue" title="Michigan clocks">
          {/* Timing rules are attorney-reviewed (invariant 7). Stated here as a
              reminder of what the notices panel on each lease actually enforces, not
              re-derived — a date computed in a dashboard that disagrees with the one
              in the notice is the worst possible version of this feature. */}
          <ul className="muted">
            <li>
              <strong>14 days</strong> from receiving a deposit to send the notice naming the bank —
              recorded from a lease's <em>Record deposit received</em> action.
            </li>
            <li>
              <strong>7 days</strong> to return the itemized move-in checklist, in two blank copies
              — recorded against the checklist once the tenant hands it back.
            </li>
            <li>
              <strong>30 days</strong> from move-out for itemized damages and the balance. Missing
              it forfeits the right to withhold and exposes the company to double damages — the
              move-out alert ladder escalates unsent damages notices at days 14, 21 and 25.
            </li>
          </ul>
          <p className="muted">
            See <Link to="/staff/leases">Leases</Link> for the notices panel on each lease — this
            console never drafts the notice text itself, since those templates are
            attorney-reviewed; staff attach the document they generated outside the app.
          </p>
        </Widget>
      ),
    },
    { id: 'reports-glance', span: 'full', content: <ReportsGlanceWidget /> },
  ];

  return <DashboardGrid storageKey="dashboard:PROPERTY_MANAGER" items={items} />;
}

function AccountingDashboard({ me }: { me: MeResponse }) {
  const pastDue = me.balances.filter((balance) => balance.maxDaysLate > 0);

  const items: DashboardItem[] = [
    {
      id: 'stats',
      span: 'full',
      content: (
        <StatGrid
          stats={[
            {
              label: 'Open charges',
              value: me.scope.openCharges,
              to: '/staff/open-charges',
              hint: 'obligations not fully allocated',
            },
            {
              label: 'Leases with a balance',
              value: me.balances.length,
            },
            {
              label: 'Past due',
              value: pastDue.length,
              hint: pastDue.length > 0 ? 'at least one day late' : 'nothing late',
            },
            // Read-only for this role: no link through to an edit screen, because
            // ACCOUNTING has inventory:read and not inventory:write. Re-pointing a
            // unit at another property moves rent between owners with no ledger entry.
            { label: 'Leases', value: me.scope.leases },
          ]}
        />
      ),
    },
    { id: 'exceptions', span: 'half', content: <ExceptionsWidget /> },
    {
      id: 'balances',
      span: 'half',
      content: (
        <Widget
          icon="ledger"
          tone="orange"
          title="Balances by lease"
          action={
            <>
              <Link to="/staff/delinquency">Delinquency</Link> ·{' '}
              <Link to="/staff/rent-roll">Rent roll</Link>
            </>
          }
        >
          <BalanceTable
            balances={me.balances}
            emptyMessage="No lease is carrying a balance."
            caption="Balances are derived from unreversed allocations, never stored. A reversal writes an NSF charge rather than deleting the allocation."
          />
        </Widget>
      ),
    },
    { id: 'open-charges', span: 'half', content: <OpenChargesWidget /> },
    { id: 'reports-glance', span: 'full', content: <ReportsGlanceWidget /> },
    {
      id: 'role-info',
      span: 'half',
      content: (
        <Widget icon="info" tone="blue" title="What this role can and cannot do">
          <p className="muted">
            Tax IDs and SSNs are readable here — that is the 1099 business need the database
            recognises. Inventory is read-only: creating and editing properties, units and
            ownerships belongs to ADMIN and PROPERTY_MANAGER, so that nobody can move a unit between
            owners and reconcile the result.
          </p>
          <p className="muted">
            ACH settles in roughly four business days and unauthorized returns can arrive much
            later, so no payment is disbursable to an owner before settlement plus the funds hold.
            Owner disbursement itself is deliberately out of scope for now.
          </p>
        </Widget>
      ),
    },
  ];

  return <DashboardGrid storageKey="dashboard:ACCOUNTING" items={items} />;
}

function MaintenanceDashboard({ me }: { me: MeResponse }) {
  const items: DashboardItem[] = [
    {
      id: 'stats',
      span: 'full',
      content: (
        // Not linked through to Tenants: this role has tenants:read, so the list
        // renders, but the landing page points at the work instead of the people.
        <StatGrid
          stats={[
            {
              label: 'Open work orders',
              value: me.scope.openWorkOrders,
              to: me.scope.openWorkOrders > 0 ? '/staff/work-orders' : undefined,
              hint: 'not yet completed or canceled',
            },
            { label: 'Properties', value: me.scope.properties, to: '/staff/properties' },
            { label: 'Units', value: me.scope.units, to: '/staff/properties' },
            { label: 'Residents', value: me.scope.tenants },
          ]}
        />
      ),
    },
    {
      id: 'access-info',
      span: 'half',
      content: (
        <Widget icon="info" tone="blue" title="Your access">
          <p className="muted">
            Addresses, units and resident names — enough to work a job — plus photo upload against a
            property, unit, or work order. Owner records, SSNs and anything financial are not
            available to this role, and the SSN restriction is enforced by the database rather than
            by this page: `tenant_sensitive` has no policy that admits MAINTENANCE, so those rows do
            not exist as far as this login is concerned.
          </p>

          {me.scope.tenantSensitive > 0 ? (
            <p className="alert alert--error" role="alert">
              <strong>Stop.</strong> This account can read {me.scope.tenantSensitive} row(s) from{' '}
              <code>tenant_sensitive</code> and should read none. That is an RLS regression, not a
              display bug — check `app_can_read_sensitive()` before going further.
            </p>
          ) : null}
        </Widget>
      ),
    },
    {
      id: 'work-orders-vendors',
      span: 'half',
      content: (
        <Widget
          icon="wrench"
          tone="green"
          title="Work orders and vendors"
          action={<Link to="/staff/work-orders">Open work orders →</Link>}
        >
          <p className="muted">
            <Link to="/staff/work-orders">Work orders</Link> — status, assignment, notes, and photos
            against each one. Assigning a <Link to="/staff/vendors">vendor</Link> emails them
            directly; that's the one place this build sends real mail. Staff-only for now — no owner
            or tenant sees any of it, and no tenant can submit a request through the portal yet.
            That would be the portal's first-ever write, which is a deliberate later decision, not
            backed into this step.
          </p>
        </Widget>
      ),
    },
  ];

  return <DashboardGrid storageKey="dashboard:MAINTENANCE" items={items} />;
}
